import { POI } from "../poi/POI";
import { Material } from "./Material";
import { PBRMaterial } from "./PBRMaterial";
import { VideoMaterial } from "./VideoMaterial";
import { ImageMaterial } from "./ImageMaterial";
import { YouTubeMaterial } from "./YouTubeMaterial";
import { TexturedMaterial } from "./TexturedMaterial";
import * as THREE from 'three';
import { getCanonicalShapeName } from '../shapes/ShapeTypes';
import { Logger } from '../core/Logger';

/**
 * 🎯 Clean material configuration with explicit roles
 */
export interface MaterialSet {
    front: Material;      // Front face material (always present)
    back?: Material;      // Back face material (optional - if different from front)
    sides?: Material;     // Side faces material (optional - for multi-material shapes)
}

/**
 * Legacy type for backward compatibility
 */
export type MaterialConfiguration = MaterialSet | Material | Material[];

/**
 * A factory class for creating and configuring materials based on POI data.
 * Clean object-oriented approach without wrapper patterns.
 */
export class MaterialFactory {
    
    private static isHlsUrl(url: string): boolean {
        if (!url) return false;
        const u = url.trim();
        return /\.m3u8(?:$|[?#])/i.test(u) || u.toLowerCase().includes('master.m3u8');
    }
    
    private static isVideoUrl(url: string): boolean {
        if (!url) return false;
        return /\.(mp4|webm|ogg)(?:$|[?#])/i.test(url) || MaterialFactory.isHlsUrl(url);
    }
    
    /**
     * Creates a primary material wrapper for a POI.
     * This is the main material that drives the UI property panel.
     * @param poi The POI to create a material for.
     * @returns A Material instance.
     */
    public static createPrimaryMaterialForPOI(poi: POI): Material {
        const poidata = poi.poidata;
        const urls = poidata.uri.value || []; // Now an array
        const url = urls[0] || ''; // Take first URI
        const isTextured = url && url.trim() !== '';

        let material: Material;
        
        if (isTextured) {
            // Check material type based on URL pattern
            const isVideo = MaterialFactory.isVideoUrl(url);
            const isYouTube = YouTubeMaterial.isYouTubeUrl(url);
            
            if (isYouTube) {
                material = new YouTubeMaterial(`${poi.name} YouTube Material`, poidata, poi);
                (material as YouTubeMaterial).setUri(url);
            } else if (isVideo) {
                material = new VideoMaterial(`${poi.name} Video Material`, poidata);
                (material as VideoMaterial).setUri(url);
            } else {
                material = new ImageMaterial(`${poi.name} Image Material`, poidata);
                (material as ImageMaterial).setUri(url);
            }
        } else {
            // Non-textured material
            const pbrMaterial = new PBRMaterial(`${poi.name} PBR Material`, poidata );
            material = pbrMaterial;
        }

        return material;
    }

    /**
     * 🎯 Clean method that returns structured material configuration
     */
    public static createMaterialsForPOI(poi: POI): MaterialSet {
        const poidata = poi.poidata;
        const urls = poidata.uri.value || []; // Now directly an array!
        const canonicalShape = getCanonicalShapeName(poidata.shape.value ?? '');
        
        // 🎯 CLEAN: Direct array access for front/back textures  
        const frontUrl = urls[0] || '';
        const backUrl = urls[1] || frontUrl; // Use front URL as fallback for back
        const hasMultipleTextures = urls.length > 1;
        
        //console.log(`🎯 [MaterialFactory] URLs: front='${frontUrl}', back='${backUrl}', hasMultiple=${hasMultipleTextures}`);
        
        const isTextured = frontUrl !== '';
        const isMultiMaterialShape = (canonicalShape === 'triangle' || canonicalShape === 'hexagon' || canonicalShape === 'roundedBox' || canonicalShape === 'tile' || canonicalShape === 'quader');

        //console.log(`🚨 [MaterialFactory] createMaterialsForPOI() CALLED!`);
        //console.log(`🔍 [MaterialFactory] POI='${poi.name}', shape='${canonicalShape}'`);
        //console.log(`  📁 urls: ${JSON.stringify(urls)} (length=${urls.length})`);
        //console.log(`  🎯 frontUrl: '${frontUrl}'`);
        //console.log(`  🎯 backUrl: '${backUrl}'`);
        //console.log(`  📊 isTextured=${isTextured}, isMultiMaterial=${isMultiMaterialShape}, hasMultipleTextures=${hasMultipleTextures}`);

        // Create primary material (uses front URL)
        let primaryMaterial: Material;
        if (isTextured) {
            // Check if it's a GLB/GLTF or USDZ file - query-aware
            const cleanFront = frontUrl.split('#')[0].split('?')[0];
            const isGLTF = /\.(glb|gltf)$/i.test(cleanFront);
            const isUSDZ = /\.usdz$/i.test(cleanFront);
            const isVideo = MaterialFactory.isVideoUrl(frontUrl);
            const isYouTube = YouTubeMaterial.isYouTubeUrl(frontUrl);
            
            if (isGLTF) {
                // GLB/GLTF files use simple PBR material - the model handles its own materials
                primaryMaterial = new PBRMaterial(`${poi.name} PBR Material`, poidata);
                //Logger.Instance.Debug(`[MaterialFactory] Created PBR material for GLB/GLTF file: ${poi.name}`);
            } else if (isUSDZ) {
                // USDZ files use simple PBR material for browser fallback - AR is handled separately
                primaryMaterial = new PBRMaterial(`${poi.name} USDZ Placeholder Material`, poidata);
                //Logger.Instance.Debug(`[MaterialFactory] Created PBR material for USDZ file: ${poi.name}`);
            } else if (isYouTube) {
                primaryMaterial = new YouTubeMaterial(`${poi.name} YouTube Material`, poidata, poi);
                (primaryMaterial as YouTubeMaterial).setUri(frontUrl);
            } else if (isVideo) {
                primaryMaterial = new VideoMaterial(`${poi.name} Video Material`, poidata);
                (primaryMaterial as VideoMaterial).setUri(frontUrl);
            } else if (poidata.shape.value === 'gltf') {
                // GLTF shapes load their asset via GLTFShape — the uri is a model, not an
                // image; feeding it to ImageMaterial only produces startup error noise.
                primaryMaterial = new PBRMaterial(`${poi.name} PBR Material`, poidata);
            } else {
                primaryMaterial = new ImageMaterial(`${poi.name} Image Material`, poidata);
                (primaryMaterial as ImageMaterial).setUri(frontUrl);
            }
        } else {
            primaryMaterial = new PBRMaterial(`${poi.name} PBR Material`, poidata);
        }

        // Setup primary material
        if (primaryMaterial instanceof PBRMaterial) {
            primaryMaterial.setupAsPrimary(poidata);
        }
        
        // For primary material, we already use frontUrl in the material creation above

        // Return single or dual materials
        const needsMultiMaterial = isMultiMaterialShape || hasMultipleTextures;
        
        //console.log(`🔍 [MaterialFactory] needsMultiMaterial=${needsMultiMaterial} (isMultiMaterialShape=${isMultiMaterialShape} || hasMultipleTextures=${hasMultipleTextures})`);
        
        if (needsMultiMaterial) {
            // Check if user wants backside to use side material (solid)
            const useBacksideSideMaterial = poidata.useBacksideSideMaterial?.value ?? false;
            
            //console.log(`🎛️ [MaterialFactory] Multi-material mode: useBacksideSideMaterial=${useBacksideSideMaterial}`);
            //console.log(`🔍 [MaterialFactory] Checking conditions: hasMultipleTextures=${hasMultipleTextures}, backUrl !== frontUrl=${backUrl !== frontUrl}`);
            
            //console.log(`🔍 [MaterialFactory] Multi-material condition check:`);
            //console.log(`  hasMultipleTextures=${hasMultipleTextures} (urls.length=${urls.length})`);
            //console.log(`  backUrl !== frontUrl: '${backUrl}' !== '${frontUrl}' = ${backUrl !== frontUrl}`);
            //console.log(`  Final condition: ${hasMultipleTextures && backUrl !== frontUrl}`);
            
            if (hasMultipleTextures && backUrl !== frontUrl) {
                // 🎯 MULTI-URI: Different logic for Box vs Tile shapes
                const isBoxShape = (canonicalShape === 'box');
                //console.log(`🎯 Creating MULTI-URI materials for ${isBoxShape ? 'BOX' : 'TILE'}: front='${frontUrl}', back='${backUrl}', sides=solid`);
                
                // Secondary material (texture 2)
                let secondaryMaterial: Material;
                const isBackVideo = MaterialFactory.isVideoUrl(backUrl);
                const isBackYouTube = YouTubeMaterial.isYouTubeUrl(backUrl);
                
                if (isBackYouTube) {
                    secondaryMaterial = new YouTubeMaterial(`${poi.name} Secondary YouTube Material`, poidata);
                    (secondaryMaterial as YouTubeMaterial).setUri(backUrl);
                } else if (isBackVideo) {
                    secondaryMaterial = new VideoMaterial(`${poi.name} Secondary Video Material`, poidata);
                    (secondaryMaterial as VideoMaterial).setUri(backUrl);
                } else {
                    secondaryMaterial = new ImageMaterial(`${poi.name} Secondary Image Material`, poidata);
                    (secondaryMaterial as ImageMaterial).setUri(backUrl);
                }
                (secondaryMaterial as TexturedMaterial).setupAsSecondary(poidata);
                
                // Side material (solid)
                const sideMaterial = new PBRMaterial(`${poi.name} Side Material`, poidata);
                // 🎯 Enable double-side rendering for solid materials so they're visible from both sides
                sideMaterial.renderBackface.value = true;
                sideMaterial.setupAsSecondary(poidata);
                
                //console.log(`🎯 Primary material: ${primaryMaterial.constructor.name}, uri='${(primaryMaterial as any).uri?.value}'`);
                //console.log(`🎯 Secondary material: ${secondaryMaterial.constructor.name}, uri='${(secondaryMaterial as any).uri?.value}'`);
                //console.log(`🎯 Solid material: ${sideMaterial.constructor.name} (solid)`);
                
                let result;
                if (isBoxShape) {
                    // Box: primary=front/back, secondary=left/right, solid=top/bottom
                    result = {
                        front: primaryMaterial,    // Material[0] → Front+Back faces
                        back: secondaryMaterial,   // Material[1] → Left+Right faces  
                        sides: sideMaterial        // Material[2] → Top+Bottom faces
                    };
                    //console.log(`🎯 BOX MaterialSet: Material[0]=primary(front/back), Material[1]=secondary(left/right), Material[2]=solid(top/bottom)`);
                } else {
                    // Tile/Quader: front=front, back=back, sides=sides
                    result = {
                        front: primaryMaterial,    // Material[0] → Front face
                        back: secondaryMaterial,   // Material[1] → Back face
                        sides: sideMaterial        // Material[2] → Side faces
                    };
                    //console.log(`🎯 TILE/QUADER MaterialSet: Material[0]=front, Material[1]=back, Material[2]=sides`);
                    //console.log(`  🔍 TILE MaterialSet created: front=${!!result.front}, back=${!!result.back}, sides=${!!result.sides}`);
                    //console.log(`  🎯 Front URI: '${(primaryMaterial as any).uri?.value}'`);
                    //console.log(`  🎯 Back URI: '${(secondaryMaterial as any).uri?.value}'`);
                    //console.log(`  🎯 Sides: ${sideMaterial.constructor.name} (solid)`);
                }
                
                //console.log(`🚀 [MaterialFactory] Returning MULTI-URI MaterialSet for ${isBoxShape ? 'BOX' : 'TILE'}`);
                return result;
                
            } else {
                // 🎯 SINGLE-URI: Standard multi-material (textured front, solid back/sides)
                const sideMaterial = new PBRMaterial(`${poi.name} Secondary`, poidata);
                // 🎯 Enable double-side rendering for solid materials so they're visible from both sides
                sideMaterial.renderBackface.value = true;
                sideMaterial.setupAsSecondary(poidata);
                
                //console.log(`🎯 Created SINGLE-URI materials for ${canonicalShape}`);
                //console.log(`🎯 Front material: ${primaryMaterial.constructor.name}, uri='${(primaryMaterial as any).uri?.value}'`);
                //console.log(`🎯 Sides material: ${sideMaterial.constructor.name} (solid)`);
                //console.log(`🎯 useBacksideSideMaterial=${useBacksideSideMaterial}, hasMultipleTextures=${hasMultipleTextures}`);
                
                const backMat = useBacksideSideMaterial ? undefined : primaryMaterial;
                //console.log(`🎯 Back material: ${backMat ? backMat.constructor.name + " (same as front)" : "undefined (uses sides)"}`);
                
                const result = {
                    front: primaryMaterial,
                    back: backMat, // Back = sides OR front
                    sides: sideMaterial
                };
                //console.log(`🚀 [MaterialFactory] Returning SINGLE-URI MaterialSet: front=${!!result.front}, back=${!!result.back}, sides=${!!result.sides}`);
                return result;
            }
        } else {
            // 🎯 SINGLE MATERIAL: Just front texture
            //console.log(`🎯 Created single material for ${canonicalShape}`);
            return {
                front: primaryMaterial
                // No back or sides - shape uses single material everywhere
            };
        }
    }

    /**
     * 🖼️ NEW: Creates layered materials for background color support
     * Returns an object that can be used by Mesh3D to create layered rendering
     */
    private static createLayeredMaterials(poi: POI, primaryMaterial: Material): any {
        const poidata = poi.poidata;
        

        const baseMaterialWrapper = new PBRMaterial(`${poi.name} Base Material`, poi.poidata);
        const baseMaterial = baseMaterialWrapper.getThreeMaterial();
        
        // Create overlay transparent material with texture
        const overlayMaterial = (primaryMaterial as TexturedMaterial).getThreeMaterial();
        if (overlayMaterial instanceof THREE.MeshStandardMaterial || overlayMaterial instanceof THREE.MeshBasicMaterial) {
            overlayMaterial.transparent = true;
            overlayMaterial.depthWrite = false; // Don't write to depth buffer to avoid z-fighting
            overlayMaterial.color.set(0xffffff); // Don't tint the texture
        }
        
        // Return a special marker object that Mesh3D can recognize for layered rendering
        return {
            __isLayeredMaterial: true,
            baseMaterial: baseMaterial,
            overlayMaterial: overlayMaterial,
            poi: poi
        };
    }

 
} 